import './DashboardPages.css';
import DashboardLayout from '../components/DashboardLayout';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Line, Bar } from 'react-chartjs-2';
import { reportsApi } from '../api/reports';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Register ChartJS components
ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend
);

function DashboardReports() {
	const { orgData } = useAuth();
	const [activeTab, setActiveTab] = useState('overview');
	const [timeframe, setTimeframe] = useState('month');
	const [reportData, setReportData] = useState({
		overview: null,
		trends: null,
		historical: null,
		metrics: null,
	});
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [startDate, setStartDate] = useState(
		new Date(new Date().setMonth(new Date().getMonth() - 1))
	);
	const [endDate, setEndDate] = useState(new Date());
	const [dayOfWeek, setDayOfWeek] = useState('');

	useEffect(() => {
		let interval;
		if (orgData?.id) {
			const fetchData = async () => {
				await fetchReportData();
			};
			fetchData();
			interval = setInterval(fetchData, 300000); // 5 minutes
		}
		return () => {
			if (interval) clearInterval(interval);
		};
	}, [orgData?.id, activeTab, timeframe]);

	const fetchReportData = async () => {
		try {
			setIsLoading(true);
			setError(null);

			const dateRange = reportsApi.getDateRange(timeframe);
			let data = {};

			switch (activeTab) {
				case 'overview':
					const [overview, trends] = await Promise.all([
						reportsApi.getOverview(orgData.id),
						reportsApi.getTrends(orgData.id, 7),
					]);
					data = { overview, trends };
					break;

				case 'historical':
					const historical = await reportsApi.getHistorical(
						orgData.id,
						{
							startDate: dateRange.startDate,
							endDate: dateRange.endDate,
						}
					);
					data = { historical };
					break;

				case 'metrics':
					const metrics = await reportsApi.getMetrics(orgData.id, {
						timeRange: timeframe,
					});
					data = { metrics };
					break;

				default:
					break;
			}

			setReportData((prev) => ({ ...prev, ...data }));
		} catch (error) {
			console.error('Error fetching report data:', error);
			setError(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleGenerateReport = async () => {
		try {
			setIsLoading(true);
			await reportsApi.generateDaily(orgData.id);
			await fetchReportData();
		} catch (error) {
			setError(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleExport = async (format = 'csv') => {
		try {
			const blob = await reportsApi.exportData(
				orgData.id,
				activeTab,
				format
			);
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${activeTab}-report.${format}`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			setError('Failed to export data');
		}
	};

	const fetchHistoricalData = async () => {
		try {
			setIsLoading(true);
			setError(null);

			const historical = await reportsApi.getHistorical(orgData.id, {
				startDate: startDate.toISOString().split('T')[0],
				endDate: endDate.toISOString().split('T')[0],
				dayOfWeek: dayOfWeek || undefined,
			});

			setReportData((prev) => {
				return { ...prev, historical };
			});
		} catch (error) {
			console.error('Error fetching historical data:', error);
			setError(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const renderOverviewSection = () => (
		<div className='overview-section'>
			<div className='reports-grid'>
				<div className='report-card'>
					<h3>Today's Check-ins</h3>
					<div className='metric-value'>
						{reportData.overview?.currentCheckIns || 0}
					</div>
					<div
						className={`metric-trend ${
							reportData.overview?.checkinTrend >= 0
								? 'positive'
								: 'negative'
						}`}>
						{reportData.overview?.checkinTrend >= 0 ? '+' : ''}
						{reportData.overview?.checkinTrend.toFixed(1)}% vs last
						week
					</div>
				</div>

				<div className='report-card'>
					<h3>Active Members</h3>
					<div className='metric-value'>
						{reportData.overview?.activeMembers || 0}
					</div>
					<div
						className={`metric-trend ${
							reportData.overview?.memberTrend >= 0
								? 'positive'
								: 'negative'
						}`}>
						{reportData.overview?.memberTrend >= 0 ? '+' : ''}
						{reportData.overview?.memberTrend.toFixed(1)}% monthly
						growth
					</div>
				</div>

				<div className='report-card'>
					<h3>Guest Users</h3>
					<div className='metric-value'>
						{reportData.overview?.guestUsers || 0}
					</div>
				</div>
			</div>

			{reportData.overview?.ministryBreakdown && (
				<div className='ministry-breakdown'>
					<h3>Ministry Check-ins</h3>
					<div className='chart-container'>
						<Bar
							data={{
								labels: reportData.overview.ministryBreakdown.map(
									(m) => m.Ministry.name
								),
								datasets: [
									{
										label: 'Check-ins',
										data: reportData.overview.ministryBreakdown.map(
											(m) => m.count
										),
										backgroundColor:
											orgData?.primaryColor || '#4b95a3',
									},
								],
							}}
							options={{
								responsive: true,
								plugins: {
									legend: {
										position: 'top',
									},
									title: {
										display: true,
										text: "Today's Check-ins by Ministry",
									},
								},
							}}
						/>
					</div>
				</div>
			)}
		</div>
	);

	const renderHistoricalSection = () => (
		<div className='historical-section'>
			<div className='date-filters'>
				<div className='date-picker-group'>
					<label>Start Date:</label>
					<DatePicker
						selected={startDate}
						onChange={(date) => setStartDate(date)}
						selectsStart
						startDate={startDate}
						endDate={endDate}
						maxDate={endDate}
						className='date-picker'
					/>
				</div>
				<div className='date-picker-group'>
					<label>End Date:</label>
					<DatePicker
						selected={endDate}
						onChange={(date) => setEndDate(date)}
						selectsEnd
						startDate={startDate}
						endDate={endDate}
						minDate={startDate}
						maxDate={new Date()}
						className='date-picker'
					/>
				</div>
				<div className='day-filter-group'>
					<label>Filter by Day:</label>
					<select
						value={dayOfWeek}
						onChange={(e) => setDayOfWeek(e.target.value)}
						className='day-select'>
						<option value=''>All Days</option>
						<option value='0'>Sunday</option>
						<option value='1'>Monday</option>
						<option value='2'>Tuesday</option>
						<option value='3'>Wednesday</option>
						<option value='4'>Thursday</option>
						<option value='5'>Friday</option>
						<option value='6'>Saturday</option>
					</select>
				</div>
				<button
					onClick={() => fetchHistoricalData()}
					className='search-button'
					style={{
						backgroundColor: orgData?.primaryColor || '#4b95a3',
					}}>
					Search Records
				</button>
			</div>

			{reportData.historical && reportData.historical.length > 0 ? (
				<>
					<div className='historical-summary'>
						<div className='report-card'>
							<h3>Total Check-ins</h3>
							<div className='metric-value'>
								{reportData.historical.reduce(
									(sum, day) =>
										sum + (day.totalCheckins || 0),
									0
								)}
							</div>
						</div>
						<div className='report-card'>
							<h3>Average Daily Check-ins</h3>
							<div className='metric-value'>
								{Math.round(
									reportData.historical.reduce(
										(sum, day) =>
											sum + (day.totalCheckins || 0),
										0
									) / reportData.historical.length
								)}
							</div>
						</div>
						<div className='report-card'>
							<h3>Peak Day</h3>
							<div className='metric-value'>
								{
									reportData.historical.reduce(
										(max, day) =>
											(day.totalCheckins || 0) >
											(max.totalCheckins || 0)
												? day
												: max,
										reportData.historical[0]
									).date
								}
							</div>
							<div className='metric-subtitle'>
								{reportData.historical.reduce(
									(max, day) =>
										(day.totalCheckins || 0) >
										(max.totalCheckins || 0)
											? day
											: max,
									reportData.historical[0]
								).totalCheckins || 0}{' '}
								check-ins
							</div>
						</div>
					</div>

					<div className='chart-container'>
						<Line
							data={{
								labels: reportData.historical.map(
									(d) => d.date
								),
								datasets: [
									{
										label: 'Total Check-ins',
										data: reportData.historical.map(
											(d) => d.totalCheckins || 0
										),
										borderColor:
											orgData?.primaryColor || '#4b95a3',
										tension: 0.1,
									},
								],
							}}
							options={{
								responsive: true,
								plugins: {
									legend: {
										position: 'top',
									},
									title: {
										display: true,
										text: 'Historical Check-in Trends',
									},
								},
							}}
						/>
					</div>

					<div className='historical-table'>
						<h3>Detailed Records</h3>
						<table>
							<thead>
								<tr>
									<th>Date</th>
									<th>Total Check-ins</th>
									<th>Active Users</th>
									<th>Guest Users</th>
									<th>Ministry Breakdown</th>
								</tr>
							</thead>
							<tbody>
								{reportData.historical.map((day) => (
									<tr key={day.date}>
										<td>{day.date}</td>
										<td>{day.totalCheckins || 0}</td>
										<td>{day.activeUsers || 0}</td>
										<td>{day.guestUsers || 0}</td>
										<td>
											{Object.entries(
												day.ministryBreakdown || {}
											)
												.map(
													([ministry, count]) =>
														`${ministry}: ${count}`
												)
												.join(', ')}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			) : (
				<div className='no-data-message'>
					No historical data available for the selected period. Please
					adjust your search criteria.
				</div>
			)}
		</div>
	);

	const renderMetricsSection = () => (
		<div className='metrics-section'>
			{reportData.metrics && (
				<>
					<div className='metrics-summary'>
						<div className='report-card'>
							<h3>Average Daily Check-ins</h3>
							<div className='metric-value'>
								{Math.round(
									reportData.metrics.metrics?.[0]
										?.totalCheckins /
										(timeframe === 'week' ? 7 : 30)
								) || 0}
							</div>
						</div>
						<div className='report-card'>
							<h3>Average Active Users</h3>
							<div className='metric-value'>
								{reportData.metrics.metrics?.[0]
									?.avgActiveUsers || 0}
							</div>
						</div>
						<div className='report-card'>
							<h3>Average Guest Users</h3>
							<div className='metric-value'>
								{reportData.metrics.metrics?.[0]
									?.avgGuestUsers || 0}
							</div>
						</div>
					</div>

					<div className='chart-container'>
						<Line
							data={{
								labels: reportData.metrics.metrics.map(
									(m) => m.period
								),
								datasets: [
									{
										label: 'Total Check-ins',
										data: reportData.metrics.metrics.map(
											(m) => m.totalCheckins
										),
										borderColor:
											orgData?.primaryColor || '#4b95a3',
										tension: 0.1,
									},
									{
										label: 'Active Users',
										data: reportData.metrics.metrics.map(
											(m) => m.avgActiveUsers
										),
										borderColor: '#2196f3',
										tension: 0.1,
										borderDash: [5, 5],
									},
								],
							}}
							options={{
								responsive: true,
								plugins: {
									legend: {
										position: 'top',
									},
									title: {
										display: true,
										text: 'Attendance Trends',
									},
								},
								scales: {
									y: {
										beginAtZero: true,
										ticks: {
											stepSize: 1,
										},
									},
								},
							}}
						/>
					</div>

					{reportData.metrics.metrics[0]?.ministryBreakdown && (
						<div className='ministry-metrics'>
							<h3>Ministry Breakdown</h3>
							<div className='ministry-grid'>
								{Object.entries(
									reportData.metrics.metrics[0]
										.ministryBreakdown
								).map(([ministry, count]) => (
									<div
										key={ministry}
										className='ministry-card'>
										<div className='ministry-name'>
											{ministry}
										</div>
										<div className='ministry-count'>
											{count}
										</div>
										<div className='ministry-label'>
											check-ins
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<div className='page-header'>
					<h1>Reports</h1>
					<div className='report-actions'>
						<select
							value={timeframe}
							onChange={(e) => setTimeframe(e.target.value)}
							className='timeframe-select'>
							<option value='week'>Last Week</option>
							<option value='month'>Last Month</option>
							<option value='quarter'>Last Quarter</option>
							<option value='year'>Last Year</option>
							<option value='yearToDate'>Year to Date</option>
						</select>
						<button
							onClick={handleGenerateReport}
							className='generate-button'
							style={{
								backgroundColor:
									orgData?.primaryColor || '#4b95a3',
							}}>
							Generate Report
						</button>
						<button
							onClick={() => handleExport('csv')}
							className='export-button'
							style={{
								backgroundColor:
									orgData?.primaryColor || '#4b95a3',
							}}>
							Export CSV
						</button>
					</div>
				</div>

				<div className='content-section'>
					<div className='reports-tabs'>
						<button
							className={`tab-button ${
								activeTab === 'overview' ? 'active' : ''
							}`}
							onClick={() => setActiveTab('overview')}
							style={{
								'--primary-color':
									orgData?.primaryColor || '#4b95a3',
							}}>
							Overview
						</button>
						<button
							className={`tab-button ${
								activeTab === 'historical' ? 'active' : ''
							}`}
							onClick={() => setActiveTab('historical')}
							style={{
								'--primary-color':
									orgData?.primaryColor || '#4b95a3',
							}}>
							Historical
						</button>
						<button
							className={`tab-button ${
								activeTab === 'metrics' ? 'active' : ''
							}`}
							onClick={() => setActiveTab('metrics')}
							style={{
								'--primary-color':
									orgData?.primaryColor || '#4b95a3',
							}}>
							Metrics
						</button>
					</div>

					{error && <div className='error-message'>{error}</div>}

					{isLoading ? (
						<div className='loading-spinner'>Loading...</div>
					) : (
						<div className='reports-content'>
							{activeTab === 'overview' &&
								renderOverviewSection()}
							{activeTab === 'historical' &&
								renderHistoricalSection()}
							{activeTab === 'metrics' && renderMetricsSection()}
						</div>
					)}
				</div>
			</div>
		</DashboardLayout>
	);
}

export default DashboardReports;
